import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { categoryFallbackImage } from "@/constants/categoryImages";

const EXCLUDED_CATEGORIES = ["Take Away"];

export default function PublicOrder() {
  const [shops, setShops] = useState([]);
  const [shop, setShop] = useState(null);
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState({});
  const [name, setName] = useState("");
  const [placedOrder, setPlacedOrder] = useState(null);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api.get("/public/shops").then(({ data }) => setShops(data));
  }, []);

  const chooseShop = async (s) => {
    setShop(s);
    setPlacedOrder(null);
    const { data } = await api.get(`/public/shops/${s.id}/menu`);
    setItems((data.items || []).filter((i) => !EXCLUDED_CATEGORIES.includes(i.category)));
  };

  const categories = ["All", ...new Set(items.map((i) => i.category))];
  const visible = category === "All" ? items : items.filter((i) => i.category === category);

  const addItem = (item) => setCart((prev) => ({ ...prev, [item.id]: { item, qty: (prev[item.id]?.qty || 0) + 1 } }));
  const removeItem = (item) => setCart((prev) => {
    const q = (prev[item.id]?.qty || 0) - 1;
    const copy = { ...prev };
    if (q <= 0) delete copy[item.id]; else copy[item.id] = { item, qty: q };
    return copy;
  });

  const lines = Object.values(cart);
  const total = lines.reduce((s, l) => s + l.item.price * l.qty, 0);

  const placeOrder = async () => {
    if (!name.trim() || lines.length === 0) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/public/orders", {
        shop_id: shop.id, customer_name: name,
        lines: lines.map((l) => ({ item_id: l.item.id, qty: l.qty })),
      });
      setPlacedOrder(data);
      setCart({});
      setName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", padding: "20px 0" }} data-testid="public-order-page">
      <div className="phone-stage">
        <div className="phone">
          <div className="phone-top">
            <div className="shopname" data-testid="phone-shop-name">{shop ? shop.name : "Choose a shop"}</div>
            <div className="hrs">{shop ? (shop.working_hours || shop.address) : "Pick where you're ordering from"}</div>
            {shop && <button className="phone-back" onClick={() => { setShop(null); setCart({}); setPlacedOrder(null); }} data-testid="phone-back-btn">‹ Change shop</button>}
          </div>

          {!shop && (
            <div className="phone-scroll">
              {shops.map((s) => (
                <button className="phone-shop-row" key={s.id} onClick={() => chooseShop(s)} data-testid={`public-shop-${s.id}`}>
                  <div><div className="sname">{s.name}</div><div className="sarea">{s.address || "—"}</div></div>
                  <div className="shrs">{s.working_hours}</div>
                </button>
              ))}
              {shops.length === 0 && <div className="empty" style={{ margin: 16 }}>No shops accepting online orders yet</div>}
            </div>
          )}

          {shop && !placedOrder && (
            <>
              <div className="phone-cats">
                {categories.map((c) => (
                  <span key={c} className={c === category ? "sel" : ""} onClick={() => setCategory(c)} data-testid={`public-cat-${c}`}>{c}</span>
                ))}
              </div>
              <div className="phone-scroll">
                <div className="menu-grid">
                  {visible.map((item) => (
                    <button
                      key={item.id} className={`menu-item ${!item.in_stock ? "out" : ""}`}
                      onClick={() => item.in_stock && addItem(item)} data-testid={`public-item-${item.id}`}
                    >
                      {!item.in_stock && <div className="out-tag">SOLD OUT</div>}
                      {cart[item.id] && <div className="qty-pill">{cart[item.id].qty}</div>}
                      <div className="thumb"><img src={item.image_url || categoryFallbackImage(item.category)} alt={item.name} /></div>
                      <div className="body"><strong>{item.name}</strong><small>₹{item.price}</small></div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="cart-bar">
                <span data-testid="cart-summary">{lines.reduce((s, l) => s + l.qty, 0)} items · ₹{total}</span>
              </div>
            </>
          )}

          {placedOrder && (
            <div className="confirm">
              <div className="lbl">Order placed</div>
              <div className="no" data-testid="confirmation-order-number">#{placedOrder.order_number}</div>
              <p>Thanks {placedOrder.customer_name}! Show this number at the counter to pay and collect your order.</p>
            </div>
          )}
        </div>
      </div>

      {shop && !placedOrder && (
        <div className="dashboard-grid" style={{ maxWidth: 700, margin: "20px auto 0", padding: "0 16px" }}>
          <div className="card">
            <div className="section-head"><div><h2>Cart &amp; name</h2></div></div>
            <div style={{ padding: 16 }}>
              {lines.length === 0 && <div className="empty">No items yet</div>}
              {lines.map((l) => (
                <div className="cart-line" key={l.item.id}>
                  <strong>{l.item.name} × {l.qty}</strong>
                  <span className="mono">₹{l.item.price * l.qty}</span>
                  <button onClick={() => removeItem(l.item)} data-testid={`public-remove-${l.item.id}`}>✕</button>
                </div>
              ))}
            </div>
            <div className="ticket-total" style={{ margin: "0 16px 16px" }}><span className="field-label">Grand total</span><strong className="mono">₹{total}</strong></div>
            <div className="field"><label className="field-label">Your name</label><input className="input" style={{ width: "100%" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Required to place order" data-testid="public-name-input" /></div>
            <div style={{ padding: 16 }}>
              <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} disabled={!name.trim() || lines.length === 0 || placing} onClick={placeOrder} data-testid="public-place-order-btn">
                Place order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}